import { useState, useEffect, useCallback } from 'react';
import { syncClaims, getLicenseInfo } from '../services/api';
import { useAuth } from './useAuth';
import { LicenseInfo } from '../types/LicenseInfo';

export function useLicense() {
  const { token } = useAuth();

  // the license itself
  const [license, setLicense] = useState<LicenseInfo | null>(null);
  // true while the *initial* load is in flight
  const [initialLoading, setInitialLoading] = useState(true);
  // only used to capture errors on that *initial* load
  const [initialError, setInitialError] = useState<Error | null>(null);

  const refresh = useCallback(
    async ({ silent = false } = {}) => {
      if (!token) return;
      if (!silent) {
        setInitialLoading(true);
        setInitialError(null);
      }
      try {
        await syncClaims();
        const info = await getLicenseInfo();
        setLicense(info);
      } catch (e: any) {
        if (!silent) {
          setInitialError(e);
        }
      } finally {
        if (!silent) {
          setInitialLoading(false);
        }
      }
    },
    [token]
  );

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    import('react-native').then(({ Platform }) => {
      if (Platform.OS !== 'web') {
        const onFocus = () => refresh({ silent: true });
        window.addEventListener('focus', onFocus);
        return () => window.removeEventListener('focus', onFocus);
      }
    });
  }, [refresh]);

  return {
    license,
    initialLoading,
    initialError,
    // alias for your SettingsPanel
    loading: initialLoading,
    error: initialError,
    refresh,
  };
}
